'use client';

import { useEffect, useRef, useCallback, useState } from 'react';

import {
  acquireSocket,
  releaseSocket,
  getSharedSocket
} from './socketSingleton';

type NewMessageEvent = {
  conversationId: number;
  message: {
    id: number;
    body: string;
    senderEmail: string;
    createdAt: string;
  };
};

type ConversationUpdateEvent = {
  conversationId: number;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadCount?: number;
};

type MessagesReadEvent = {
  conversationId: number;
  readByUserId: number;
  readByFirstName: string;
  readAt: string;
};

type ForceLogoutEvent = {
  reason: string;
  timestamp: string;
};

type VideoCallInviteEvent = {
  conversationId: number;
  callerId: number;
  callerName: string;
  roomName: string;
};

type VideoCallAcceptedEvent = {
  conversationId: number;
  acceptedByUserId: number;
};

type VideoCallDeclinedEvent = {
  conversationId: number;
  declinedByUserId: number;
};

type VideoCallEndedEvent = {
  conversationId: number;
  endedByUserId: number;
};

export type ApplicationSubmittedEvent = {
  submissionId: number;
  conversationId: number | null;
  applicationTitle: string;
  submitterName: string | null;
};

export type WebSocketCallbacks = {
  onNewMessage?: (event: NewMessageEvent) => void;
  onConversationUpdate?: (event: ConversationUpdateEvent) => void;
  onMessagesRead?: (event: MessagesReadEvent) => void;
  onForceLogout?: (event: ForceLogoutEvent) => void;
  onVideoCallInvite?: (event: VideoCallInviteEvent) => void;
  onVideoCallAccepted?: (event: VideoCallAcceptedEvent) => void;
  onVideoCallDeclined?: (event: VideoCallDeclinedEvent) => void;
  onVideoCallEnded?: (event: VideoCallEndedEvent) => void;
  onApplicationSubmitted?: (event: ApplicationSubmittedEvent) => void;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
};

type UseWebSocketOptions = {
  userId: number | null | undefined;
  callbacks?: WebSocketCallbacks;
  enabled?: boolean;
};

type UseWebSocketReturn = {
  isConnected: boolean;
  subscribeToConversation: (conversationId: number) => void;
  unsubscribeFromConversation: (conversationId: number) => void;
  reconnect: () => void;
};

export function useWebSocket({
  userId,
  callbacks,
  enabled = true
}: UseWebSocketOptions): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!enabled || !userId) return;

    const socket = acquireSocket();
    if (!socket) return;

    const onConnect = () => {
      setIsConnected(true);
      socket.emit('join-user-room', userId);
      callbacksRef.current?.onConnect?.();
    };

    const onDisconnect = (reason: string) => {
      setIsConnected(false);
      callbacksRef.current?.onDisconnect?.(reason);
    };

    const onNewMessage = (data: NewMessageEvent) => {
      callbacksRef.current?.onNewMessage?.(data);
    };

    const onConversationUpdate = (data: ConversationUpdateEvent) => {
      callbacksRef.current?.onConversationUpdate?.(data);
    };

    const onMessagesRead = (data: MessagesReadEvent) => {
      callbacksRef.current?.onMessagesRead?.(data);
    };

    const onForceLogout = (data: ForceLogoutEvent) => {
      callbacksRef.current?.onForceLogout?.(data);
    };

    const onVideoCallInvite = (data: VideoCallInviteEvent) => {
      callbacksRef.current?.onVideoCallInvite?.(data);
    };

    const onVideoCallAccepted = (data: VideoCallAcceptedEvent) => {
      callbacksRef.current?.onVideoCallAccepted?.(data);
    };

    const onVideoCallDeclined = (data: VideoCallDeclinedEvent) => {
      callbacksRef.current?.onVideoCallDeclined?.(data);
    };

    const onVideoCallEnded = (data: VideoCallEndedEvent) => {
      callbacksRef.current?.onVideoCallEnded?.(data);
    };

    const onApplicationSubmitted = (data: ApplicationSubmittedEvent) => {
      callbacksRef.current?.onApplicationSubmitted?.(data);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('new-message', onNewMessage);
    socket.on('conversation-update', onConversationUpdate);
    socket.on('messages-read', onMessagesRead);
    socket.on('force-logout', onForceLogout);
    socket.on('video-call-invite', onVideoCallInvite);
    socket.on('video-call-accepted', onVideoCallAccepted);
    socket.on('video-call-declined', onVideoCallDeclined);
    socket.on('video-call-ended', onVideoCallEnded);
    socket.on('application-submitted', onApplicationSubmitted);

    if (!socket.connected) {
      socket.connect();
    } else {
      setIsConnected(true);
      socket.emit('join-user-room', userId);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('new-message', onNewMessage);
      socket.off('conversation-update', onConversationUpdate);
      socket.off('messages-read', onMessagesRead);
      socket.off('force-logout', onForceLogout);
      socket.off('video-call-invite', onVideoCallInvite);
      socket.off('video-call-accepted', onVideoCallAccepted);
      socket.off('video-call-declined', onVideoCallDeclined);
      socket.off('video-call-ended', onVideoCallEnded);
      socket.off('application-submitted', onApplicationSubmitted);
      setIsConnected(false);
      releaseSocket();
    };
  }, [userId, enabled]);

  const subscribeToConversation = useCallback((conversationId: number) => {
    const socket = getSharedSocket();
    if (socket?.connected) {
      socket.emit('subscribe-conversation', conversationId);
    }
  }, []);

  const unsubscribeFromConversation = useCallback((conversationId: number) => {
    const socket = getSharedSocket();
    if (socket?.connected) {
      socket.emit('unsubscribe-conversation', conversationId);
    }
  }, []);

  const reconnect = useCallback(() => {
    const socket = getSharedSocket();
    if (socket) {
      socket.disconnect();
      setTimeout(() => socket.connect(), 100);
    }
  }, []);

  return {
    isConnected,
    subscribeToConversation,
    unsubscribeFromConversation,
    reconnect
  };
}
